<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';

const { currentAccount, updateAccount } = useAccount();

const settings = computed(() => currentAccount.value?.settings || {});

// 本地编辑副本(从账户设置初始化, 保存时回写 dustess_* 键)
const config = ref({
  baseUrl: '',
  enabled: false,
  clientId: '',
  ownerOrPool: 'pool', // owner | pool
  ownerId: '',
  poolId: '',
  autoPush: false, // 自动推送(留电即推), 默认关
  onlyWithPhone: true, // 数据过滤: 只推有手机号
  chatRecordBaseUrl: '', // 会话记录h5页域名(可选, 留空用当前后台域名)
});
// secret 单独: 不预填(掩码), 仅在用户输入时回写, 避免清空已存值
const secretInput = ref('');
const secretConfigured = computed(() => !!settings.value.dustess_client_secret);

const saving = ref(false);
const savedAt = ref('');
const error = ref('');

const initFromSettings = () => {
  const s = settings.value;
  config.value = {
    baseUrl: s.dustess_base_url || 'https://qw-openapi-tx.dustess.com',
    enabled: s.dustess_enabled === true || s.dustess_enabled === 'true',
    clientId: s.dustess_client_id || '',
    ownerOrPool: s.dustess_owner_or_pool || 'pool',
    ownerId: s.dustess_owner_id || '',
    poolId: s.dustess_pool_id || '',
    autoPush: s.dustess_auto_push === true || s.dustess_auto_push === 'true',
    onlyWithPhone:
      s.dustess_only_with_phone === undefined
        ? true
        : s.dustess_only_with_phone === true ||
          s.dustess_only_with_phone === 'true',
    chatRecordBaseUrl: s.dustess_chat_record_base_url || '',
  };
};

onMounted(initFromSettings);

const save = async () => {
  saving.value = true;
  error.value = '';
  try {
    const payload = {
      dustess_base_url: config.value.baseUrl,
      dustess_enabled: config.value.enabled,
      dustess_client_id: config.value.clientId,
      dustess_pool_id: config.value.poolId,
      dustess_owner_id: config.value.ownerId,
      dustess_owner_or_pool: config.value.ownerOrPool,
      dustess_auto_push: config.value.autoPush,
      dustess_only_with_phone: config.value.onlyWithPhone,
      dustess_chat_record_base_url: config.value.chatRecordBaseUrl,
    };
    // 仅在用户输入新 secret 时回写, 避免覆盖已存值
    if (secretInput.value.trim()) {
      payload.dustess_client_secret = secretInput.value.trim();
    }
    await updateAccount(payload);
    secretInput.value = '';
    await initFromSettings();
    savedAt.value = new Date().toLocaleString('zh-CN', {
      timeZone: 'Asia/Shanghai',
    });
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || '保存失败';
  } finally {
    saving.value = false;
  }
};

// 字段映射: Chatwoot 名片字段 -> 尘锋联系人字段
const mappings = [
  { cw: 'name(姓名/访客id)', dust: 'remark(联系人名称,必填)' },
  { cw: 'phone_number(手机号)', dust: 'mobiles[type=mobile]' },
  { cw: 'email(邮箱)', dust: 'email' },
  { cw: 'city(IP地域)', dust: 'prov_city(省市区)' },
  { cw: 'contact.id(Chatwoot主键)', dust: 'external_id(去重用,全局唯一)' },
  { cw: 'custom_attributes.owner(归属者)', dust: '所属者(wuevjn-m96y2364)' },
  {
    cw: 'custom_attributes.referrer_url(来源页)',
    dust: '网站地址(tdx78f-lwyjuz1g)',
  },
  {
    cw: '会话记录h5页(本系统生成, 必选)',
    dust: '易聊-对话记录(zya1ai-m25uwy9w)',
  },
];
</script>

<template>
  <div class="flex flex-col gap-5 p-6 w-full h-full overflow-y-auto">
    <div class="flex items-center justify-between">
      <h2 class="text-lg font-semibold text-n-slate-12">名片推送配置</h2>
      <div class="flex items-center gap-3">
        <span
          v-if="savedAt"
          class="text-xs text-n-slate-10 hidden sm:inline"
          >已保存 · {{ savedAt }}</span
        >
        <!-- 自动推送开关(默认关) -->
        <button
          type="button"
          class="flex items-center gap-2 h-9 px-3 rounded-md text-sm font-medium transition-colors"
          :class="
            config.autoPush
              ? 'bg-green-500 text-white'
              : 'bg-n-alpha-2 text-n-slate-11'
          "
          @click="config.autoPush = !config.autoPush"
        >
          <span
            class="i-lucide-zap size-4"
            :class="config.autoPush ? 'text-white' : ''"
          ></span>
          自动推送{{ config.autoPush ? '·已开启' : '' }}
        </button>
      </div>
    </div>

    <!-- 尘锋接口配置 -->
    <section
      class="flex flex-col gap-4 p-4 rounded-lg border border-n-weak bg-n-surface-1"
    >
      <h3 class="text-sm font-medium text-n-slate-12">第三方平台接口配置</h3>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <label class="flex flex-col gap-1 text-sm text-n-slate-11 sm:col-span-2">
          接口地址
          <input
            v-model="config.baseUrl"
            class="h-9 px-3 rounded-md border border-n-weak bg-transparent text-n-slate-12 outline-none focus:border-n-brand"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          启用状态
          <select
            v-model="config.enabled"
            class="h-9 px-2 rounded-md border border-n-weak bg-transparent text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option :value="false">未启用</option>
            <option :value="true">已启用</option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11 lg:col-span-2">
          ClientID
          <input
            v-model="config.clientId"
            class="h-9 px-3 rounded-md border border-n-weak bg-transparent text-n-slate-12 outline-none focus:border-n-brand"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11 lg:col-span-2">
          ClientSecret
          <input
            v-model="secretInput"
            type="password"
            :placeholder="secretConfigured ? '已配置, 留空则不修改' : '请输入'"
            class="h-9 px-3 rounded-md border border-n-weak bg-transparent text-n-slate-12 outline-none focus:border-n-brand"
          />
        </label>
      </div>
    </section>

    <!-- 归属 / 推送行为 -->
    <section
      class="flex flex-col gap-4 p-4 rounded-lg border border-n-weak bg-n-surface-1"
    >
      <h3 class="text-sm font-medium text-n-slate-12">推送设置</h3>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          归属方式
          <select
            v-model="config.ownerOrPool"
            class="h-9 px-2 rounded-md border border-n-weak bg-transparent text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option value="pool">推入公海</option>
            <option value="owner">指派主跟进人</option>
          </select>
        </label>
        <label
          v-if="config.ownerOrPool === 'owner'"
          class="flex flex-col gap-1 text-sm text-n-slate-11"
        >
          主跟进人ID(尘锋员工id)
          <input
            v-model="config.ownerId"
            class="h-9 px-3 rounded-md border border-n-weak bg-transparent text-n-slate-12 outline-none focus:border-n-brand"
          />
        </label>
        <label v-else class="flex flex-col gap-1 text-sm text-n-slate-11">
          公海ID
          <input
            v-model="config.poolId"
            class="h-9 px-3 rounded-md border border-n-weak bg-transparent text-n-slate-12 outline-none focus:border-n-brand"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11 sm:col-span-2">
          会话记录页域名(可选, 留空用当前后台域名)
          <input
            v-model="config.chatRecordBaseUrl"
            placeholder="https://chat.example.com"
            class="h-9 px-3 rounded-md border border-n-weak bg-transparent text-n-slate-12 outline-none focus:border-n-brand"
          />
        </label>
      </div>
      <div class="flex flex-wrap gap-6">
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="config.onlyWithPhone" type="checkbox" class="size-4" />
          数据过滤(仅推有手机号)
        </label>
      </div>
    </section>

    <!-- 字段映射 -->
    <section
      class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak bg-n-surface-1"
    >
      <h3 class="text-sm font-medium text-n-slate-12">配置属性 · 字段映射</h3>
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="text-n-slate-10">
            <tr class="border-b border-n-weak">
              <th class="py-2 text-left font-normal">Chatwoot 名片字段</th>
              <th class="py-2 text-left font-normal">尘锋联系人字段</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="m in mappings"
              :key="m.cw"
              class="border-b border-n-weak/60 text-n-slate-12"
            >
              <td class="py-2 pr-4">{{ m.cw }}</td>
              <td class="py-2">{{ m.dust }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div v-if="error" class="text-xs text-n-amber-12">{{ error }}</div>
    <div class="flex gap-2">
      <button
        type="button"
        class="h-9 px-4 rounded-md text-sm font-medium text-white bg-n-brand hover:opacity-90 disabled:opacity-60"
        :disabled="saving"
        @click="save"
      >
        {{ saving ? '保存中...' : '保存配置' }}
      </button>
    </div>
  </div>
</template>
